import { create } from 'zustand';
import type { RunRecord, HistoryItem, TaskConfiguration } from '../types';

const HISTORY_KEY = 'academic-review-history';
const CONFIG_KEY = 'academic-review-config';
const LANG_KEY = 'academic-review-lang';

function loadHistory(): HistoryItem[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
  } catch { return []; }
}

function saveHistory(items: HistoryItem[]) {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(items.slice(0, 50))); } catch { /* noop */ }
}

function getDefaultYearTo(): number {
  return new Date().getFullYear();
}

function getDefaultYearFrom(): number {
  return getDefaultYearTo() - 4;
}

function loadConfig(): TaskConfiguration {
  const defaults: TaskConfiguration = {
    topic: '', helperSearchTerms: [], excludedTerms: [],
    yearFrom: getDefaultYearFrom(), yearTo: getDefaultYearTo(), language: 'all',
    journalRanks: [], minImpactFactor: null,
    sources: ['openalex', 'crossref', 'semantic_scholar'],
    reviewType: 'full', wordCount: 5000, citationStyle: 'IEEE',
    outputFormats: ['markdown', 'bibtex'], enableKgExtraction: true, noSearch: false, outputDraftOnly: false,
  };
  try {
    return { ...defaults, ...JSON.parse(localStorage.getItem(CONFIG_KEY) || '{}') };
  } catch { return defaults; }
}

let pendingConfigSave: TaskConfiguration | null = null;
let configSaveTimer: ReturnType<typeof setTimeout> | null = null;

function saveConfigNow(config: TaskConfiguration) {
  try { localStorage.setItem(CONFIG_KEY, JSON.stringify(config)); } catch { /* noop */ }
}

function flushConfigSave() {
  if (!pendingConfigSave) return;
  const next = pendingConfigSave;
  pendingConfigSave = null;
  if (configSaveTimer) {
    clearTimeout(configSaveTimer);
    configSaveTimer = null;
  }
  saveConfigNow(next);
}

function scheduleConfigSave(config: TaskConfiguration) {
  pendingConfigSave = config;
  if (configSaveTimer) clearTimeout(configSaveTimer);
  configSaveTimer = setTimeout(flushConfigSave, 300);
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushConfigSave);
}

function loadLang(): string {
  try { return localStorage.getItem(LANG_KEY) || 'zh'; } catch { return 'zh'; }
}

function saveLang(lang: string) {
  try { localStorage.setItem(LANG_KEY, lang); } catch { /* noop */ }
}

interface AppState {
  currentRun: RunRecord | null;
  setCurrentRun: (run: RunRecord | null) => void;

  history: HistoryItem[];
  setHistory: (items: HistoryItem[]) => void;
  addToHistory: (item: HistoryItem) => void;
  removeFromHistory: (runId: string) => void;
  clearHistory: () => void;

  config: TaskConfiguration;
  setConfig: (config: Partial<TaskConfiguration>) => void;

  activeView: 'workspace' | 'history';
  setActiveView: (view: 'workspace' | 'history') => void;
  language: 'zh' | 'en';
  setLanguage: (lang: 'zh' | 'en') => void;

  sseConnected: boolean;
  setSseConnected: (connected: boolean) => void;
}

export const useAppStore = create<AppState>((set) => ({
  currentRun: null,
  setCurrentRun: (run) => set({ currentRun: run }),

  history: loadHistory(),
  setHistory: (items) => set(() => {
    const updated = items.slice(0, 200);
    saveHistory(updated);
    return { history: updated };
  }),
  addToHistory: (item) => set((s) => {
    const updated = [item, ...s.history.filter(h => h.runId !== item.runId)].slice(0, 200);
    saveHistory(updated);
    return { history: updated };
  }),
  removeFromHistory: (runId) => set((s) => {
    const updated = s.history.filter(h => h.runId !== runId);
    saveHistory(updated);
    return { history: updated };
  }),
  clearHistory: () => set(() => {
    saveHistory([]);
    return { history: [] };
  }),

  config: loadConfig(),
  setConfig: (partial) => set((s) => {
    const updated = { ...s.config, ...partial };
    scheduleConfigSave(updated);
    return { config: updated };
  }),

  activeView: 'workspace',
  setActiveView: (view) => set({ activeView: view }),
  language: loadLang() as 'zh' | 'en',
  setLanguage: (lang) => { saveLang(lang); set({ language: lang }); },

  sseConnected: false,
  setSseConnected: (connected) => set({ sseConnected: connected }),
}));
